import { useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../types/graph';

export const GraphAssertions = () => {
  const [assertionMode, setAssertionMode] = useState<
    'Raw' | 'By Response' | 'Assembled'
  >('Raw');
  const graph = useSelector((state: RootState) => state.graph);

  const assembleGraph = (assertions: string[]) => {
    if (!assertions || assertions.length === 0) {
      return 'No assertions available';
    }

    const sourceMap: Record<string, string[]> = {};

    assertions.forEach((str) => {
      const match = str.match(/\[(.*?)\]-(.*?)->\[(.*?)\]/);
      if (match) {
        const source = match[1].trim();
        const rest = `-${match[2]}->[${match[3]}]`;

        if (!sourceMap[source]) {
          sourceMap[source] = [];
        }
        sourceMap[source].push(rest);
      }
    });

    let output = '';
    Object.entries(sourceMap).forEach(([source, relations]) => {
      output += `[${source}]\n`;

      relations.forEach((relation) => {
        output += `    ${relation}\n`;

        const match = relation.match(/-\(.*?\)->\[(.*?)\]/);
        if (match && match[1]) {
          const childEntity = match[1];
          if (sourceMap[childEntity]) {
            sourceMap[childEntity].forEach((childRelation) => {
              output += `        ${childRelation}\n`;

              const matchChild = childRelation.match(/-\(.*?\)->\[(.*?)\]/);
              if (matchChild && matchChild[1]) {
                const grandchildEntity = matchChild[1];
                if (sourceMap[grandchildEntity]) {
                  sourceMap[grandchildEntity].forEach((grandchildRelation) => {
                    output += `            ${grandchildRelation}\n`;
                  });
                }
              }
            });
          }
        }
      });

      output += '\n';
    });

    return output || 'No structured data could be generated';
  };

  return (
    <div className="bg-[var(--dbg-bg)] p-4 text-[var(--dbg-text)]">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-medium text-[var(--dbg-text)]">
            Assertion Mode
          </h3>
          <div className="flex flex-row gap-4">
            {(['Raw', 'By Response', 'Assembled'] as const).map((mode) => (
              <label
                key={mode}
                className="flex items-center gap-2 cursor-pointer text-[var(--dbg-text)]"
              >
                <input
                  type="radio"
                  name="assertionMode"
                  value={mode}
                  checked={assertionMode === mode}
                  onChange={(e) =>
                    setAssertionMode(
                      e.target.value as 'Raw' | 'By Response' | 'Assembled',
                    )
                  }
                  className="accent-[var(--dbg-accent)]"
                />
                {mode}
              </label>
            ))}
          </div>
        </div>

        <div>
          <div>
            {assertionMode === 'Raw' && (
              <div className="max-h-[95vh] overflow-y-auto p-2">
                <textarea
                  readOnly
                  rows={14}
                  className="w-full bg-[var(--dbg-bg-code)] text-[var(--dbg-text)] border border-[var(--dbg-border)] rounded px-3 py-2 text-[13px] font-mono focus:outline-none resize-none"
                  value={graph.assertions?.join('\n') || ''}
                />
                <div className="mt-4 no-scrollbar">
                  <textarea
                    readOnly
                    rows={14}
                    className="w-full bg-[var(--dbg-bg-code)] text-[var(--dbg-text)] border border-[var(--dbg-border)] rounded px-3 py-2 text-[13px] font-mono focus:outline-none resize-none"
                    value={
                      graph.values
                        ? Object.entries(graph.values)
                            .map(([key, valueObj]) => {
                              const value = valueObj?.value ?? '';
                              const unit =
                                valueObj?.unit?.plural ||
                                valueObj?.unit?.singular ||
                                '';
                              return `• ${key} : ${value}${
                                unit ? ' ' + unit : ''
                              }`;
                            })
                            .join('\n')
                        : ''
                    }
                  />
                </div>
              </div>
            )}
            {assertionMode === 'By Response' && (
              <div className="max-h-[95vh] overflow-y-auto no-scrollbar">
                {graph.assertionsByResponse &&
                Object.keys(graph.assertionsByResponse).length > 0 ? (
                  Object.entries(graph.assertionsByResponse).map(
                    ([responseId, assertions]) => (
                      <div key={responseId} className="mb-6">
                        <h3 className="text-lg font-bold text-[var(--dbg-accent)] mb-1">
                          {responseId}
                        </h3>
                        <div className="ml-4">
                          {assertions.map((assertion, index) => (
                            <p
                              key={index}
                              className="mb-1 text-sm text-[var(--dbg-text)] font-mono leading-relaxed"
                            >
                              • {assertion}
                            </p>
                          ))}
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <p className="text-sm text-[var(--dbg-text-secondary)]">
                    No assertions by response found
                  </p>
                )}
              </div>
            )}
            {assertionMode === 'Assembled' && (
              <div className="max-h-[95vh] overflow-y-auto no-scrollbar">
                {graph.assertions && graph.assertions.length > 0 ? (
                  <div>
                    <p className="text-sm text-[var(--dbg-text)] font-mono leading-relaxed whitespace-pre-wrap">
                      {assembleGraph(graph.assertions)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-[var(--dbg-text-secondary)]">
                    No assertions found to assemble
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
